import { useState } from 'react'
import { useR2List } from '../hooks/useR2Browser'
import { Card } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Folder, File, ChevronRight } from 'lucide-react'
import { R2ObjectContentViewer } from './R2ObjectContentViewer'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

interface ScopedBreadcrumbProps {
  prefix: string
  basePrefix: string
  lockedToBase: boolean
  onNavigate: (p: string) => void
}

function ScopedBreadcrumb({ prefix, basePrefix, lockedToBase, onNavigate }: ScopedBreadcrumbProps) {
  // When lockedToBase, the breadcrumb is rooted at basePrefix and you can't navigate above it.
  const root = lockedToBase ? basePrefix : ''
  const relative = prefix.slice(root.length)
  const parts = relative.split('/').filter(Boolean)

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          {parts.length > 0 ? (
            <BreadcrumbLink className="cursor-pointer" onClick={() => onNavigate(root)}>
              {root || 'Root'}
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage>{root || 'Root'}</BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {parts.map((part, i) => {
          const subPrefix = root + parts.slice(0, i + 1).join('/') + '/'
          const isLast = i === parts.length - 1
          return (
            <BreadcrumbItem key={subPrefix}>
              <BreadcrumbSeparator />
              {isLast ? (
                <BreadcrumbPage>{part}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink className="cursor-pointer" onClick={() => onNavigate(subPrefix)}>
                  {part}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export interface R2ScopedBrowserProps {
  bucket: string
  basePrefix: string
  /** If true, breadcrumb root is clamped at basePrefix and can't escape upward. */
  lockedToBase?: boolean
}

/**
 * Reusable R2 browser scoped to a base prefix. Click a folder to descend,
 * click a file to open the inline content viewer (uses /api/r2-browser/object/content).
 */
export function R2ScopedBrowser({ bucket, basePrefix, lockedToBase = false }: R2ScopedBrowserProps) {
  const [prefix, setPrefix] = useState(basePrefix)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const { result, loading, error } = useR2List(bucket, prefix)

  const folders = (result?.delimitedPrefixes || []).map(p => {
    const name = p.slice(prefix.length).replace(/\/$/, '')
    return { prefix: p, name }
  })
  const files = result?.objects || []

  return (
    <div>
      <div className="mb-4">
        <ScopedBreadcrumb
          prefix={prefix}
          basePrefix={basePrefix}
          lockedToBase={lockedToBase}
          onNavigate={setPrefix}
        />
      </div>

      <R2ObjectContentViewer
        open={!!selectedKey}
        onOpenChange={(open) => { if (!open) setSelectedKey(null) }}
        bucket={bucket}
        objectKey={selectedKey}
      />

      {error && (
        <div className="text-sm text-destructive mb-4">Failed to list objects: {error}</div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : folders.length === 0 && files.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          {prefix === basePrefix ? 'Nothing here yet.' : 'This directory is empty.'}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-28 text-right">Size</TableHead>
                <TableHead className="w-44">Last Modified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {folders.map(f => (
                <TableRow
                  key={f.prefix}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setPrefix(f.prefix)}
                >
                  <TableCell className="w-8 px-3">
                    <Folder className="size-4 text-amber-500" />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1">
                      {f.name}
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                </TableRow>
              ))}
              {files.map(obj => {
                const fileName = obj.key.slice(prefix.length)
                return (
                  <TableRow
                    key={obj.key}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedKey(obj.key)}
                  >
                    <TableCell className="w-8 px-3">
                      <File className="size-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{fileName}</span>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                      {formatBytes(obj.size)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(obj.lastModified)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}
